import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '../services/api';
import './NotificationsDropdown.css';

export interface Notification {
  id: number;
  userId: number | null;
  messageId: string | null;
  type: 'info' | 'warning' | 'error' | 'success' | 'announcement' | 'system_update';
  category: 'message' | 'system_update' | 'quiz' | 'account';
  title: string | null;
  message: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  isRead: boolean;
  readAt: Date | null;
  createdAt: string;
  expiresAt: string | null;
  metadata: Record<string, any>;
}

interface NotificationsDropdownProps {
  userId: number | null;
  userRole: string;
}

const NotificationsDropdown: React.FC<NotificationsDropdownProps> = ({ userId, userRole }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Fetch notifications and unread count
  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const [notificationsRes, countRes] = await Promise.all([
        authAPI.getNotifications({ limit: 10, unreadOnly: false }),
        authAPI.getUnreadCount()
      ]);
      
      setNotifications(notificationsRes.data.notifications || []);
      setUnreadCount(countRes.data.count || 0);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  // Poll for unread count every 2 minutes (reduced from 30 seconds for efficiency)
  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(() => {
      authAPI.getUnreadCount()
        .then(res => setUnreadCount(res.data.count || 0))
        .catch(err => console.error('Error fetching unread count:', err));
    }, 120000); // 2 minutes (120,000ms)

    return () => clearInterval(interval);
  }, [userId, userRole]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Refresh notifications when dropdown opens
  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
  }, [isOpen]);

  const handleMarkAsRead = async (notificationId: number, e?: React.MouseEvent) => {
    e?.stopPropagation();
    try {
      await authAPI.markNotificationAsRead(notificationId);
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, isRead: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleMarkAllAsRead = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await authAPI.markAllNotificationsAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.isRead) {
      handleMarkAsRead(notification.id);
    }
    setIsOpen(false);
    navigate('/notifications');
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'error': return 'ERR';
      case 'warning': return 'WRN';
      case 'success': return 'OK';
      case 'announcement': return 'ANN';
      case 'system_update': return 'SYS';
      default: return 'INF';
    }
  };

  const getPriorityClass = (priority: string) => {
    return `priority-${priority}`;
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="notifications-dropdown" ref={dropdownRef}>
      <button
        className="notifications-bell"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Notifications"
      >
        <i className="fas fa-bell bell-icon"></i>
        {unreadCount > 0 && (
          <span className="notification-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
        )}
      </button>

      {isOpen && (
        <div className="notifications-panel">
          <div className="notifications-header">
            <h3>Notifications</h3>
            {unreadCount > 0 && (
              <button
                className="mark-all-read-btn"
                onClick={handleMarkAllAsRead}
                title="Mark all as read"
              >
                Mark all read
              </button>
            )}
            <button
              className="view-all-btn"
              onClick={() => {
                setIsOpen(false);
                navigate('/notifications');
              }}
            >
              View all
            </button>
          </div>

          <div className="notifications-list">
            {loading ? (
              <div className="notifications-loading">Loading...</div>
            ) : notifications.length === 0 ? (
              <div className="notifications-empty">No notifications</div>
            ) : (
              notifications.map(notification => (
                <div
                  key={notification.id}
                  className={`notification-item ${notification.isRead ? 'read' : 'unread'}`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className="notification-content">
                    <div className="notification-header-row">
                      <span className={`notification-type-icon type-${notification.type}`}>
                        {getTypeLabel(notification.type)}
                      </span>
                      {notification.title && (
                        <span className="notification-title">{notification.title}</span>
                      )}
                      {!notification.isRead && (
                        <span className={`notification-priority-dot ${getPriorityClass(notification.priority)}`} />
                      )}
                    </div>
                    <div className="notification-message">{notification.message}</div>
                    <div className="notification-footer">
                      <span className="notification-time">
                        {formatTime(notification.createdAt)}
                      </span>
                      {notification.category === 'system_update' && (
                        <span className="notification-category">System</span>
                      )}
                    </div>
                  </div>
                  {!notification.isRead && (
                    <button
                      className="mark-read-btn"
                      onClick={(e) => handleMarkAsRead(notification.id, e)}
                      title="Mark as read"
                    />
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationsDropdown;

